import { createElement, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { PayPalEnv, PayPalTokenizePayload } from "@/api/paypal";
import { renderPayPalButton } from "@/api/paypal/api";
import { useLogger } from "@/hooks/useLogger";
import {
  isInShadowDom,
  PaymentButtonSlotIds,
  PaymentButtonSlots,
} from "@/utils/shadow-dom";

type PayPalButtonProps = {
  /** Braintree client token from start transaction response. */
  clientToken: string;
  /**
   * Braintree environment ("sandbox" or "production") returned by the BE in the
   * start-transaction response. Forwarded to PayPal Checkout.js so the popup
   * targets the same environment the client token was issued for.
   */
  env?: PayPalEnv;
  /** Transaction amount (e.g. from amount form). */
  amount: string;
  /** Transaction currency code (e.g. selectedProcessingCurrency.code). */
  currencyCode: string;
  /** Called with payload containing nonce when the user completes the PayPal flow. */
  onSuccess: (payload: PayPalTokenizePayload) => void;
  onError?: (error: Error) => void;
  /** Called when the user cancels the PayPal popup. */
  onCancel?: () => void;
  containerId?: string;
  /** When true, button is non-interactive and visually dimmed (e.g. payment in progress or amount invalid). */
  disabled?: boolean;
};

export const PayPalButton = ({
  clientToken,
  env,
  amount,
  currencyCode,
  onSuccess,
  onError,
  onCancel,
  containerId = "paypal-button-container",
  disabled = false,
}: PayPalButtonProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const dummyRef = useRef<HTMLDivElement>(null);
  const logger = useLogger();

  const onSuccessRef = useRef(onSuccess);
  const onCancelRef = useRef(onCancel);
  const onErrorRef = useRef(onError);
  onSuccessRef.current = onSuccess;
  onCancelRef.current = onCancel;
  onErrorRef.current = onError;

  const amountNum = Number(amount) || 0;

  useEffect(() => {
    if (dummyRef.current && isInShadowDom(dummyRef.current)) {
      const el = document.getElementById(PaymentButtonSlotIds.paypal);
      setPortalTarget(el as HTMLElement | null);
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !clientToken || !currencyCode) return;

    container.innerHTML = "";
    const abortController = new AbortController();
    let cleanup: (() => void) | undefined;

    renderPayPalButton({
      clientToken,
      env,
      amount: amountNum,
      currencyCode,
      container,
      onApprove: (p) => onSuccessRef.current(p),
      onCancel: () => onCancelRef.current?.(),
      onError: (err) =>
        onErrorRef.current?.(
          err instanceof Error ? err : new Error(String(err))
        ),
      signal: abortController.signal,
      logger,
    })
      .then((cleanupFn) => {
        if (abortController.signal.aborted) {
          cleanupFn?.();
          return;
        }
        cleanup = cleanupFn;
      })
      .catch((err) => {
        onErrorRef.current?.(
          err instanceof Error ? err : new Error(String(err))
        );
      });

    return () => {
      abortController.abort();
      cleanup?.();
      container.innerHTML = "";
    };
    // portalTarget: when in shadow DOM, the first render targets a container that
    // is replaced once the portal resolves. Re-running ensures the button renders
    // into the actual light-DOM container instead of a detached node.
  }, [clientToken, env, currencyCode, amountNum, portalTarget, logger]);

  const buttonContainer = (
    <div
      id={containerId}
      ref={containerRef}
      style={{
        width: "100%",
        minHeight: 56,
        height: "100%",
        ...(disabled && {
          pointerEvents: "none",
          opacity: 0.6,
        }),
      }}
    />
  );

  if (portalTarget) {
    return (
      <>
        <div ref={dummyRef} style={{ display: "none" }} />
        {createElement("slot", { name: PaymentButtonSlots.paypal })}
        {createPortal(buttonContainer, portalTarget)}
      </>
    );
  }

  return (
    <>
      <div ref={dummyRef} style={{ display: "none" }} />
      {buttonContainer}
    </>
  );
};
